/**
 * en-US money, percent and date formatting for the Lien Filings and Private
 * Equity page groups (admin-portal).
 *
 * One implementation, so the seven routes in those two groups print a dollar
 * amount and a date the same way. Before this module the lien pages printed
 * `$` + toFixed(2) with no thousands separator and called toLocaleDateString()
 * with no locale, while the PE pages used Intl in en-US.
 *
 * It lives in pages/lien because that is the owned surface of the lane that
 * wrote it. Promoting it to src/utils/format.ts is the natural next step once a
 * third page group needs it.
 *
 * Money follows the kit rule the monitoring pages follow: a tile prints
 * compact ({@link formatMoneyCompact}, '$180M'), a table cell prints two
 * decimals ({@link formatMoney}, '$180,000,000.00'). A USD figure goes through
 * the kit's own formatters, so a PE tile and a monitoring tile print alike.
 * The PE pages printed whole dollars on both (UI run r3, fi-cons-1).
 */

import { formatUsdCompact, MONEY_UNAVAILABLE } from '@aaimplatform/ui';

/** What a cell prints when the value is absent. Never a measured zero. */
export const NOT_RECORDED = '--';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format an amount in an ISO 4217 currency, en-US grouping.
 *
 * A currency code Intl does not recognize (the PE fund record carries currency
 * as a free string) prints as the code followed by the grouped amount rather
 * than throwing a RangeError into the render.
 */
export function formatMoney(amount: number, currency = 'USD', fractionDigits = 2): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}`;
  }
}

/**
 * A headline amount in compact notation: '$180M', '€4.2M'. USD is the kit's
 * formatUsdCompact. Another ISO 4217 code gets the same notation; a code Intl
 * does not recognize prints as the code followed by the compact amount. A
 * value that is not a finite number prints the kit's 'Unavailable', never a
 * figure.
 */
export function formatMoneyCompact(amount: number, currency = 'USD'): string {
  if (!Number.isFinite(amount)) return MONEY_UNAVAILABLE;
  if (currency === 'USD') return formatUsdCompact(amount);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(amount);
  } catch {
    return `${currency} ${new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount)}`;
  }
}

/** Format integer cents (the UCC fee contract's unit) as dollars and cents. */
export function formatCents(cents: number, currency = 'USD'): string {
  return formatMoney(cents / 100, currency, 2);
}

/** Format a ratio (0.25) as a percent (25.0%). */
export function formatPercent(ratio: number, fractionDigits = 1): string {
  return `${(ratio * 100).toFixed(fractionDigits)}%`;
}

/**
 * Format a date as 'Sep 27, 2026'.
 *
 * A date-only value (OpenAPI `format: date`) names a calendar day. Handing it to
 * `new Date()` reads it as UTC midnight, which prints the previous day anywhere
 * west of UTC, so it is formatted in UTC. A date-time value is formatted in the
 * viewer's zone. Absent or unparseable input prints NOT_RECORDED.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return NOT_RECORDED;
  const dateOnly = DATE_ONLY.test(value);
  const parsed = new Date(dateOnly ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) return NOT_RECORDED;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  });
}

/**
 * Sum amounts per currency. Amounts in different currencies are never added
 * together: no conversion rate is available on these pages, and a sum across
 * currencies printed in one of them is a figure nothing measured.
 */
export function totalsByCurrency<T>(
  rows: readonly T[],
  amount: (row: T) => number,
  currency: (row: T) => string,
): Array<{ currency: string; total: number }> {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const code = currency(row);
    totals.set(code, (totals.get(code) ?? 0) + amount(row));
  }
  return Array.from(totals, ([code, total]) => ({ currency: code, total })).sort((a, b) =>
    a.currency.localeCompare(b.currency),
  );
}

/** Print per-currency totals, one figure per currency. Empty input prints NOT_RECORDED. */
export function formatCurrencyTotals(
  totals: ReadonlyArray<{ currency: string; total: number }>,
  fractionDigits = 0,
): string {
  if (totals.length === 0) return NOT_RECORDED;
  return totals.map((t) => formatMoney(t.total, t.currency, fractionDigits)).join(' + ');
}

/** Per-currency totals for a tile, each compact: '€50 + $1.2M'. Empty input prints NOT_RECORDED. */
export function formatCurrencyTotalsCompact(
  totals: ReadonlyArray<{ currency: string; total: number }>,
): string {
  if (totals.length === 0) return NOT_RECORDED;
  return totals.map((t) => formatMoneyCompact(t.total, t.currency)).join(' + ');
}
